import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useLanguage, SupportedLang } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { isAuthenticated } from '../../utils/tokenManager';

const LANG_OPTIONS: { code: SupportedLang; label: string; cc: string }[] = [
  { code: 'ar', label: 'العربية', cc: 'sa' },
  { code: 'en', label: 'English', cc: 'gb' },
  { code: 'yo', label: 'Yorùbá', cc: 'ng' },
  { code: 'ha', label: 'Hausa', cc: 'ng' },
  { code: 'hi', label: 'हिन्दी', cc: 'in' },
  { code: 'es', label: 'Español', cc: 'es' },
  { code: 'bn', label: 'বাংলা', cc: 'bd' },
];

function Flag({ cc, className }: { cc: string; className?: string }) {
  return (
    <img
      src={`https://flagcdn.com/w40/${cc}.png`}
      srcSet={`https://flagcdn.com/w40/${cc}.png 1x, https://flagcdn.com/w80/${cc}.png 2x`}
      width={20}
      height={14}
      alt=""
      className={className || 'dx-lang-switch__flag-img'}
      loading="lazy"
      decoding="async"
    />
  );
}

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile] = useUpdateProfileMutation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (next: SupportedLang) => {
    setOpen(false);
    const previous = currentLanguage;
    changeLanguage(next);
    if (!isAuthenticated()) return;
    (async () => {
      try {
        await updateProfile({ language: next }).unwrap();
      } catch {
        changeLanguage(previous);
        toast.error(t('navigation.languageSaveError'));
      }
    })();
  };

  const active = LANG_OPTIONS.find((o) => o.code === currentLanguage) || LANG_OPTIONS[0];

  return (
    <div ref={ref} className="relative dx-lang-switch">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="dx-lang-switch__btn relative inline-flex items-center justify-center gap-1.5 min-w-[3.5rem] h-9 px-2.5 rounded-lg bg-transparent text-neutral-light border border-brand-accent/45 transition-all duration-200 hover:bg-brand-accent/10 hover:border-brand-accent/70 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        aria-label={t('navigation.language')}
        aria-expanded={open}
      >
        <Flag cc={active.cc} />
        <span className="dx-lang-switch__code font-heading font-semibold text-xs uppercase tracking-wider leading-none">
          {active.code.toUpperCase()}
        </span>
      </button>
      {open && (
        <div className="absolute end-0 top-full mt-2 w-48 animate-slide-down rounded-lg card-glass shadow-2xl overflow-hidden z-[80]">
          {LANG_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => select(lang.code)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-start text-sm transition-colors duration-200 ${
                lang.code === currentLanguage
                  ? 'bg-brand-accent/20 text-brand-accent'
                  : 'text-neutral-light hover:bg-brand-accent/10 hover:text-brand-accent'
              }`}
            >
              <Flag cc={lang.cc} />
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
